import { useEffect, useRef, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import type { OutlookFolderLink } from "@gb/contracts";
import { OutlookMark } from "@/components/gb/brand-marks";
import { Button } from "@/components/ui/button";
import { documentKeys, importKeys } from "@/features/project/documents-api";
import { useSyncOutlookNow } from "@/features/project/outlook-api";
import { formatMoment } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * How the project's Outlook folders stand, at the top of Sources
 * (`plans/outlook-sync.md`, question 27): how many folders sync, from whose
 * mailboxes, when they were last checked, what is on its way, and why a
 * folder fails when one does. A failing folder is always shown, with its
 * owner, because only they can fix it. The emails not taken are listed, each
 * with why, as `SharepointLine`'s files are.
 */
export function OutlookLine({
  projectId,
  folders,
  checking = false,
  onManage,
}: {
  projectId: string;
  folders: OutlookFolderLink[];
  /** A poll is queued or running: its answer is on its way. */
  checking?: boolean;
  onManage: () => void;
}) {
  const syncNow = useSyncOutlookNow(projectId);
  const [open, setOpen] = useState(false);
  const [showNotTaken, setShowNotTaken] = useState(false);
  useArrivals(projectId, folders);

  const failing = folders.filter((folder) => folder.health === "failing");
  const waiting = folders.reduce((sum, folder) => sum + folder.waiting, 0);
  const checked = folders
    .map((folder) => folder.lastCheckedAt)
    .filter((at): at is string => at !== null)
    .sort()
    .at(-1);
  const notTaken = folders.flatMap((folder) => folder.notTaken);
  const said =
    failing.length > 0
      ? `${failing.length === 1 ? "1 folder isn’t" : `${failing.length} folders aren’t`} being reached.`
      : !checked
        ? "Reading the folders for the first time."
        : `Last checked ${formatMoment(checked)}.${
            waiting > 0 ? ` ${waiting === 1 ? "1 email is" : `${waiting} emails are`} on the way.` : ""
          }`;

  return (
    <section
      aria-label="Outlook"
      className={cn(
        "mb-4 rounded-card-lg border-[1.5px] px-5 py-3",
        failing.length > 0 ? "border-danger/40 bg-danger-bg" : "border-border bg-surface",
      )}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <OutlookMark className="size-4 shrink-0" />
        <p
          className={cn(
            "min-w-0 flex-1 text-[0.86rem]",
            failing.length > 0 ? "font-semibold text-danger" : "text-ink-slate",
          )}
          role="status"
          aria-live="polite"
        >
          <span className="font-semibold text-ink">
            {folders.length === 1 ? "1 Outlook folder" : `${folders.length} Outlook folders`}
          </span>
          {" · "}
          {said}
        </p>
        <Button variant="secondary" size="sm" disabled={syncNow.isPending || checking} onClick={() => syncNow.mutate()}>
          {(syncNow.isPending || checking) && <Loader2 size={14} className="animate-spin" />}
          {checking ? "Checking…" : failing.length > 0 ? "Try now" : "Sync now"}
        </Button>
        <Button variant="secondary" size="sm" onClick={onManage}>
          Manage
        </Button>
      </div>

      {failing.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1 pl-8 text-[0.8rem] text-danger">
          {failing.map((folder) => (
            <li key={folder.id}>
              <span className="font-semibold">{folder.folderName}</span>
              <span> · {whose(folder)} · </span>
              {folder.failingSince && <span>Not reached since {formatMoment(folder.failingSince)}. </span>}
              {folder.failure}
            </li>
          ))}
        </ul>
      )}

      {notTaken.length > 0 && (
        <div className="mt-2">
          <button
            type="button"
            aria-expanded={showNotTaken}
            onClick={() => setShowNotTaken((was) => !was)}
            className="flex cursor-pointer items-center gap-1 text-[0.8rem] font-semibold text-muted hover:text-ink"
          >
            <ChevronDown size={14} aria-hidden className={cn("transition-transform", showNotTaken && "rotate-180")} />
            {/* An email refused, or part of a kept one: its content encrypted, or
                an attachment that is only a link (questions 16 and 17). */}
            {notTaken.length === 1
              ? "1 email or part of one is not taken"
              : `${notTaken.length} emails or parts of emails are not taken`}
          </button>
          {showNotTaken && (
            <ul className="mt-1.5 flex flex-col gap-1 pl-5 text-[0.8rem] text-ink-slate">
              {notTaken.map((email, index) => (
                <li key={index}>
                  <span className="font-semibold text-ink">{email.subject || "(no subject)"}</span>
                  <span className="text-muted"> · {email.folder}</span>
                  <span className="text-muted"> · {email.reason}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mt-2">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
          className="flex cursor-pointer items-center gap-1 text-[0.8rem] font-semibold text-muted hover:text-ink"
        >
          <ChevronDown size={14} aria-hidden className={cn("transition-transform", open && "rotate-180")} />
          Which folders, and whose
        </button>
        {open && (
          <ul className="mt-1.5 flex flex-col gap-1 pl-5 text-[0.8rem] text-ink-slate">
            {folders.map((folder) => (
              <li key={folder.id}>
                <span className="font-semibold text-ink">{folder.folderName}</span>
                <span className="text-muted"> · {whose(folder)}</span>
                <span className="text-muted">
                  {" · "}
                  {folder.emails === 1 ? "1 email" : `${folder.emails} emails`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {syncNow.error && (
        <p role="alert" className="mt-2 text-[0.85rem] font-semibold text-danger">
          {syncNow.error.message}
        </p>
      )}
    </section>
  );
}

/**
 * "Your mailbox", or the owner's: who can fix a folder, and whose mail it is.
 * The GroundBrain name first, then the Microsoft account in brackets when it
 * says something more (the owner, 2026-10-18): "Václav Hrubý’s mailbox
 * (Vašek Hrubý)".
 */
export function whose(folder: Pick<OutlookFolderLink, "mine" | "owner" | "ownerAccount">): string {
  if (folder.mine) return "your mailbox";
  const { owner, ownerAccount } = folder;
  if (!owner) return ownerAccount ? `${ownerAccount}’s mailbox` : "a mailbox whose owner left";
  return ownerAccount && ownerAccount !== owner ? `${owner}’s mailbox (${ownerAccount})` : `${owner}’s mailbox`;
}

/**
 * The shelves, the files and the imports are read again when the folders'
 * counts move: emails arrive minutes after anybody here did anything, so
 * nothing else would say they had (as `SharepointLine`'s).
 */
function useArrivals(projectId: string, folders: OutlookFolderLink[]): void {
  const queryClient = useQueryClient();
  const seen = useRef<string | null>(null);
  const key = folders.map((folder) => `${folder.id}:${folder.emails}/${folder.waiting}`).join(",");
  useEffect(() => {
    if (seen.current === null) {
      seen.current = key;
      return;
    }
    if (seen.current === key) return;
    seen.current = key;
    void queryClient.invalidateQueries({ queryKey: ["project", projectId, "brain"] });
    void queryClient.invalidateQueries({ queryKey: documentKeys.list(projectId) });
    void queryClient.invalidateQueries({ queryKey: importKeys.list(projectId) });
  }, [key, projectId, queryClient]);
}
